import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { S, Field } from '../utils/styles';
import { useAuth } from '../App';

const STATUS_COLORS = {
  draft:{bg:'#f3f4f6',color:'#6b7280'},
  sent:{bg:'#fffbeb',color:'#f59e0b'},
  partial:{bg:'#eff6ff',color:'#3b82f6'},
  received:{bg:'#f0fdf4',color:'#10b981'}
};

function autoPoNumber() {
  const now = new Date();
  const yy = String(now.getFullYear()).slice(2);
  const mm = String(now.getMonth()+1).padStart(2,'0');
  const rand = String(Math.floor(Math.random()*900)+100);
  return `PO-${yy}${mm}-${rand}`;
}

export default function PurchaseOrders() {
  const { user } = useAuth();
  const [pos, setPos] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showReceipt, setShowReceipt] = useState(null);
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletedPOs, setDeletedPOs] = useState([]);
  const [showMaterialMgr, setShowMaterialMgr] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [materialEditForm, setMaterialEditForm] = useState({});
  const [savingMaterial, setSavingMaterial] = useState(false);
  const [deletingMaterial, setDeletingMaterial] = useState(null);
  const [showAddMaterial, setShowAddMaterial] = useState(false);
  const [addMaterialForm, setAddMaterialForm] = useState({ name:'', category:'Coil', spec1:'', spec2:'', unit:'kg' });
  const [addingMaterial, setAddingMaterial] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const DEFAULT_BILL_TO = 'PP Impex India\nPlot No. 7&8, Shree Industrial Estate,\nOpp. Palghar Taluka Industrial Estate,\nBoisar Road, Palghar (W), 401404\nGSTN: 27AAAPJ8406B1Z6';
  const DEFAULT_SHIP_TO = 'PP Impex India\nPlot No. 7&8, Shree Industrial Estate,\nOpp. Palghar Taluka Industrial Estate,\nBoisar Road, Palghar (W), 401404\nGSTN: 27AAAPJ8406B1Z6';

  const [form, setForm] = useState({
    supplier_id:'', po_number:'',
    po_date: new Date().toISOString().split('T')[0],
    payment_terms:'', expected_delivery:'', terms_conditions:'',
    prepared_by:'', bill_to: DEFAULT_BILL_TO, ship_to: DEFAULT_SHIP_TO,
    gst_percent:'', items:[]
  });
  const [supForm, setSupForm] = useState({ name:'', contact_name:'', phone:'', email:'', address:'', gst_number:'', materials_supplied:'' });
  const [receipt, setReceipt] = useState({
    po_item_id:'', qty_received:'',
    receipt_date: new Date().toISOString().split('T')[0],
    challan_number:'', invoice_number:'', notes:''
  });
  const [saving, setSaving] = useState(false);
  const [showNewMat, setShowNewMat] = useState(null);
  const [newMatForm, setNewMatForm] = useState({ name:'', category:'Coil', unit:'kg', spec1:'' });

  useEffect(() => { load(); }, []);

  const load = async () => {
    const [p,s,m] = await Promise.all([
      api.get('/inventory/purchase-orders'),
      api.get('/inventory/suppliers'),
      api.get('/inventory/materials')
    ]);
    setPos(p.data); setSuppliers(s.data); setMaterials(m.data);
  };

  const openNewPO = async () => {
    // Fetch sequential PO number from backend
    let poNum = autoPoNumber();
    try { const { data } = await api.get('/inventory/purchase-orders/next-number'); poNum = data.po_number; } catch(e) {}
    setForm({ supplier_id:'', po_number: poNum, po_date: new Date().toISOString().split('T')[0],
      payment_terms:'', expected_delivery:'', terms_conditions:'',
      prepared_by:'', bill_to: DEFAULT_BILL_TO, ship_to: DEFAULT_SHIP_TO,
      gst_percent:'', items:[] });
    setShowForm(true);
  };

  const addItem = () => setForm(f => ({
    ...f, items:[...f.items, {raw_material_id:'', qty_ordered:'', unit:'', unit_price:'', hsn_code:'', notes:''}]
  }));
  const removeItem = i => setForm(f => ({...f, items:f.items.filter((_,idx) => idx!==i)}));
  const updateItem = (i,k,v) => setForm(f => ({...f, items:f.items.map((it,idx) => idx===i ? {...it,[k]:v} : it)}));

  // Auto-fill unit when material selected
  const onMaterialSelect = (i, matId) => {
    updateItem(i, 'raw_material_id', matId);
    const mat = materials.find(m => m.id == matId);
    if (mat) updateItem(i, 'unit', mat.unit);
  };

  const addNewMaterial = async () => {
    if (!addMaterialForm.name.trim()) return alert('Name required');
    setAddingMaterial(true);
    try {
      await api.post('/inventory/materials', addMaterialForm);
      const { data } = await api.get('/inventory/materials');
      setMaterials(data);
      setAddMaterialForm({ name:'', category:'Coil', spec1:'', spec2:'', unit:'kg' });
      setShowAddMaterial(false);
    } catch (e) {
      alert(e.response?.data?.error || 'Error adding material');
    } finally {
      setAddingMaterial(false);
    }
  };

  const startEditMaterial = (m) => {
    setEditingMaterial(m.id);
    setMaterialEditForm({ name: m.name, category: m.category, spec1: m.spec1||'', spec2: m.spec2||'', unit: m.unit, consumption_mode: m.consumption_mode });
  };

  const saveMaterialEdit = async () => {
    setSavingMaterial(true);
    try {
      await api.put(`/inventory/materials/${editingMaterial}`, materialEditForm);
      const { data } = await api.get('/inventory/materials');
      setMaterials(data);
      setEditingMaterial(null);
    } catch (e) {
      alert(e.response?.data?.error || 'Error saving material');
    } finally {
      setSavingMaterial(false);
    }
  };

  const deleteMaterial = async (m) => {
    if (!window.confirm(`Delete "${m.name}"? Only possible if it's never been used anywhere.`)) return;
    setDeletingMaterial(m.id);
    try {
      await api.delete(`/inventory/materials/${m.id}`);
      const { data } = await api.get('/inventory/materials');
      setMaterials(data);
    } catch (e) {
      alert(e.response?.data?.error || 'Error deleting material');
    } finally {
      setDeletingMaterial(null);
    }
  };

  const deletePO = async (id) => {
    if (!window.confirm('Delete this purchase order? It can be restored by the Owner within 7 days, then it\'s gone for good.')) return;
    try { await api.delete(`/inventory/purchase-orders/${id}`); load(); }
    catch(e) { alert(e.response?.data?.error || 'Error deleting PO'); }
  };

  const loadDeleted = async () => {
    try { const { data } = await api.get('/inventory/purchase-orders/deleted'); setDeletedPOs(data); setShowDeleted(true); }
    catch(e) { alert(e.response?.data?.error || 'Error loading deleted POs'); }
  };

  const restorePO = async (id) => {
    try {
      await api.post(`/inventory/purchase-orders/${id}/restore`);
      setDeletedPOs(d => d.filter(p => p.id !== id));
      load();
    } catch(e) { alert(e.response?.data?.error || 'Error restoring PO'); }
  };

  const downloadPDF = async (poId) => {
    try {
      const { data } = await api.get(`/inventory/purchase-orders/${poId}/pdf`, { responseType:'text' });
      const blob = new Blob([data], { type:'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch(e) { alert('Error downloading PDF'); }
  };

  const savePO = async () => {
    if (!form.supplier_id || !form.po_number) return alert('Select a supplier and confirm PO number');
    if (!form.prepared_by || !form.prepared_by.trim()) return alert('"Prepared by" is required');
    if (form.gst_percent === '' || form.gst_percent === null || form.gst_percent === undefined) return alert('GST % is required (enter 0 if not applicable)');
    const validItems = form.items.filter(i => i.raw_material_id && i.qty_ordered);
    if (validItems.length === 0) return alert('Add at least one material before saving the PO');
    setSaving(true);
    try {
      await api.post('/inventory/purchase-orders', {
        ...form,
        items: form.items.filter(i => i.raw_material_id && i.qty_ordered)
      });
      load(); setShowForm(false);
    } catch(e) {
      const msg = e.response?.data?.error || 'Error saving PO';
      alert(msg);
    } finally { setSaving(false); }
  };

  const saveSupplier = async () => {
    if (!supForm.name) return;
    try {
      const {data} = await api.post('/inventory/suppliers', supForm);
      setSuppliers(s => [...s, data]);
      setShowSupplierForm(false);
      setSupForm({ name:'', contact_name:'', phone:'', email:'', address:'', gst_number:'', materials_supplied:'' });
      setForm(f => ({...f, supplier_id: String(data.id)}));
    } catch(e) { alert('Error saving supplier'); }
  };

  const openDetail = async po => {
    const {data} = await api.get(`/inventory/purchase-orders/${po.id}`);
    setShowDetail(data);
  };

  const markSent = async id => {
    await api.put(`/inventory/purchase-orders/${id}/status`, {status:'sent'});
    load();
    if (showDetail) openDetail({id});
  };

  const saveReceipt = async () => {
    try {
      const res = await api.post(`/inventory/purchase-orders/${showDetail.po.id}/receipt`, receipt);
      alert(res.data.po_fully_received ? '✅ PO fully received! Stock updated.' : 'Receipt saved. PO is partially complete.');
      openDetail({id:showDetail.po.id}); load(); setShowReceipt(null);
      setReceipt({ po_item_id:'', qty_received:'', receipt_date:new Date().toISOString().split('T')[0], challan_number:'', invoice_number:'', notes:'' });
    } catch(e) { alert(e.response?.data?.error || 'Error saving receipt'); }
  };

  const visiblePOs = pos.filter(po => {
    if (statusFilter !== 'all' && (po.status || 'draft') !== statusFilter) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      if (!po.po_number?.toLowerCase().includes(q) && !po.supplier_name?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:600, margin:0 }}>Purchase Orders</h1>
          <div style={{ fontSize:13, color:'#6b7280', marginTop:3 }}>Track raw material orders from suppliers. Receiving stock updates inventory automatically.</div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {user?.role === 'owner' && (
            <button onClick={loadDeleted} style={S.btnSecondary}>🗑 Recently deleted</button>
          )}
          <button onClick={() => setShowMaterialMgr(true)} style={S.btnSecondary}>Manage materials</button>
          <button onClick={() => setShowSupplierForm(true)} style={S.btnSecondary}>+ Add supplier</button>
          <button onClick={openNewPO} style={S.btnPrimary}>+ New PO</button>
        </div>
      </div>

      {showDeleted && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50 }} onClick={() => setShowDeleted(false)}>
          <div style={{ background:'#fff', borderRadius:10, padding:20, width:520, maxHeight:'80vh', overflowY:'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
              <div style={{ fontSize:15, fontWeight:600 }}>Recently deleted (last 7 days)</div>
              <button onClick={() => setShowDeleted(false)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:16 }}>✕</button>
            </div>
            {deletedPOs.length === 0 && <div style={{ color:'#9ca3af', fontSize:13 }}>Nothing deleted in the last 7 days.</div>}
            {deletedPOs.map(po => (
              <div key={po.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid #f1f2f5' }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:600 }}>{po.po_number} — {po.supplier_name}</div>
                  <div style={{ fontSize:12, color:'#9ca3af' }}>Deleted {new Date(po.deleted_at.replace(' ','T')+'Z').toLocaleString('en-IN', { dateStyle:'medium', timeStyle:'short' })}</div>
                </div>
                <button onClick={() => restorePO(po.id)} style={S.btnSecondary}>Restore</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {showMaterialMgr && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50 }} onClick={() => { setShowMaterialMgr(false); setEditingMaterial(null); }}>
          <div style={{ background:'#fff', borderRadius:10, padding:20, width:640, maxHeight:'80vh', overflowY:'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
              <div style={{ fontSize:15, fontWeight:600 }}>Raw materials</div>
              <button onClick={() => { setShowMaterialMgr(false); setEditingMaterial(null); }} style={{ background:'none', border:'none', cursor:'pointer', fontSize:16 }}>✕</button>
            </div>
            <div style={{ fontSize:12, color:'#9ca3af', marginBottom:10 }}>
              {user?.role === 'owner' ? 'These are the materials available to select on a PO. Edit or delete here.' : 'These are the materials available to select on a PO. Ask the Owner to edit or remove any of these.'}
            </div>
            <button onClick={() => setShowAddMaterial(s => !s)} style={{ ...S.btnSm, background:'#eff6ff', color:'#3b82f6', marginBottom:10 }}>
              {showAddMaterial ? 'Cancel' : '+ Add material'}
            </button>
            {showAddMaterial && (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:6, marginBottom:14, padding:10, background:'#f9fafb', borderRadius:8 }}>
                <input value={addMaterialForm.name} onChange={e => setAddMaterialForm(f=>({...f,name:e.target.value}))} placeholder="Name *" style={{ ...S.input, fontSize:12, padding:'4px 8px' }} />
                <select value={addMaterialForm.category} onChange={e => setAddMaterialForm(f=>({...f,category:e.target.value}))} style={{ ...S.input, fontSize:12, padding:'4px 8px' }}>
                  {['Coil','Rubber','Fastener','Box','Chemical','Other'].map(c => <option key={c}>{c}</option>)}
                </select>
                <input value={addMaterialForm.spec1} onChange={e => setAddMaterialForm(f=>({...f,spec1:e.target.value}))} placeholder="Spec 1" style={{ ...S.input, fontSize:12, padding:'4px 8px' }} />
                <select value={addMaterialForm.unit} onChange={e => setAddMaterialForm(f=>({...f,unit:e.target.value}))} style={{ ...S.input, fontSize:12, padding:'4px 8px' }}>
                  {['kg','tons','pcs','m','rolls','ghonis'].map(u => <option key={u}>{u}</option>)}
                </select>
                <button onClick={addNewMaterial} disabled={addingMaterial} style={{ ...S.btnSm, background:'#3b82f6', color:'#fff', gridColumn:'span 4' }}>
                  {addingMaterial ? 'Adding...' : 'Add material'}
                </button>
              </div>
            )}
            {materials.map(m => (
              <div key={m.id} style={{ padding:'8px 0', borderBottom:'1px solid #f3f4f6' }}>
                {editingMaterial === m.id ? (
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:6, alignItems:'center' }}>
                    <input value={materialEditForm.name} onChange={e => setMaterialEditForm(f=>({...f,name:e.target.value}))} style={{ ...S.input, fontSize:12, padding:'4px 8px' }} placeholder="Name" />
                    <input value={materialEditForm.category} onChange={e => setMaterialEditForm(f=>({...f,category:e.target.value}))} style={{ ...S.input, fontSize:12, padding:'4px 8px' }} placeholder="Category" />
                    <input value={materialEditForm.spec1} onChange={e => setMaterialEditForm(f=>({...f,spec1:e.target.value}))} style={{ ...S.input, fontSize:12, padding:'4px 8px' }} placeholder="Spec 1" />
                    <input value={materialEditForm.unit} onChange={e => setMaterialEditForm(f=>({...f,unit:e.target.value}))} style={{ ...S.input, fontSize:12, padding:'4px 8px' }} placeholder="Unit" />
                    <div style={{ gridColumn:'span 4', display:'flex', gap:6, marginTop:4 }}>
                      <button onClick={saveMaterialEdit} disabled={savingMaterial} style={{ ...S.btnSm, background:'#3b82f6', color:'#fff' }}>{savingMaterial?'Saving...':'Save'}</button>
                      <button onClick={() => setEditingMaterial(null)} style={{ ...S.btnSm, background:'none', color:'#6b7280' }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600 }}>{m.name}</div>
                      <div style={{ fontSize:11, color:'#9ca3af' }}>{m.category} · {[m.spec1,m.spec2].filter(Boolean).join(' × ')} · {m.unit}</div>
                    </div>
                    {user?.role === 'owner' && (
                      <div style={{ display:'flex', gap:6 }}>
                        <button onClick={() => startEditMaterial(m)} style={{ ...S.btnSm, background:'#f3f4f6' }}>Edit</button>
                        <button onClick={() => deleteMaterial(m)} disabled={deletingMaterial===m.id} style={{ ...S.btnSm, background:'#fef2f2', color:'#ef4444' }}>
                          {deletingMaterial===m.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            {materials.length === 0 && <div style={{ fontSize:13, color:'#9ca3af', padding:'10px 0' }}>No materials yet.</div>}
          </div>
        </div>
      )}

      {suppliers.length === 0 && (
        <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:8, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#92400e' }}>
          ⚠️ No suppliers added yet. Click <strong>"+ Add supplier"</strong> first, then create a PO.
        </div>
      )}

      <div style={{ display:'flex', gap:10, alignItems:'center', marginBottom:14, flexWrap:'wrap' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search PO number or supplier..."
          style={{ ...S.input, width:260 }}
        />
        <div style={{ display:'flex', gap:6 }}>
          {['all','draft','sent','partial','received'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              style={{
                padding:'6px 12px', borderRadius:6, fontSize:12, fontWeight:600, cursor:'pointer',
                border: statusFilter===s ? '1px solid #3b82f6' : '1px solid #e5e7eb',
                background: statusFilter===s ? '#eff6ff' : '#fff',
                color: statusFilter===s ? '#3b82f6' : '#6b7280',
                textTransform:'capitalize',
              }}
            >{s}</button>
          ))}
        </div>
        <div style={{ fontSize:12, color:'#9ca3af', marginLeft:'auto' }}>{visiblePOs.length} of {pos.length}</div>
      </div>

      <div style={{ background:'#fff', borderRadius:10, border:'1px solid #e5e7eb', overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr>{['PO Number','Supplier','Date','Expected delivery','Items','Status',''].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>
            {visiblePOs.map(po => {
              const sc = STATUS_COLORS[po.status] || STATUS_COLORS.draft;
              return (
                <tr key={po.id} style={{ borderBottom:'1px solid #f3f4f6' }}>
                  <td style={{ ...S.td, color:'#3b82f6', fontWeight:600 }}>{po.po_number}</td>
                  <td style={S.td}>{po.supplier_name}</td>
                  <td style={{ ...S.td, color:'#6b7280' }}>{po.po_date}</td>
                  <td style={{ ...S.td, color:'#6b7280' }}>{po.expected_delivery || '—'}</td>
                  <td style={{ ...S.td, color:'#6b7280' }}>{po.item_count}</td>
                  <td style={S.td}>
                    <span style={{ background:sc.bg, color:sc.color, padding:'3px 9px', borderRadius:20, fontSize:11, fontWeight:500, textTransform:'capitalize' }}>{po.status}</span>
                  </td>
                  <td style={S.td}>
                    <div style={{ display:'flex', gap:6 }}>
                      <button onClick={() => openDetail(po)} style={{ ...S.btnSm, background:'#f3f4f6', color:'#374151' }}>View</button>
                      {po.status !== 'received' && <button onClick={() => deletePO(po.id)} style={{ ...S.btnSm, background:'#fef2f2', color:'#ef4444' }}>Delete</button>}
                    </div>
                  </td>
                </tr>
              );
            })}
            {pos.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign:'center', padding:40, color:'#9ca3af' }}>
                No purchase orders yet. Create one once you've added a supplier.
              </td></tr>
            )}
            {pos.length > 0 && visiblePOs.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign:'center', padding:40, color:'#9ca3af' }}>
                No purchase orders match your search or filter.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* New PO Modal */}
      {showForm && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, width:780, maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ fontSize:16, fontWeight:600, marginBottom:20 }}>New Purchase Order</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
              <div>
                <label style={S.label}>Supplier *</label>
                <div style={{ display:'flex', gap:6 }}>
                  <select value={form.supplier_id} onChange={e => setForm(f=>({...f,supplier_id:e.target.value}))} style={{ ...S.input, flex:1 }}>
                    <option value="">Select supplier...</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  <button onClick={() => setShowSupplierForm(true)} style={{ ...S.btnSm, background:'#f3f4f6', color:'#374151', whiteSpace:'nowrap' }}>+ New</button>
                </div>
              </div>
              <div>
                <label style={S.label}>PO Number * <span style={{ fontSize:11, color:'#9ca3af' }}>(auto-generated, editable)</span></label>
                <input value={form.po_number} onChange={e => setForm(f=>({...f,po_number:e.target.value}))} style={S.input} />
              </div>
              <div><label style={S.label}>PO Date *</label><input type="date" value={form.po_date} onChange={e => setForm(f=>({...f,po_date:e.target.value}))} style={S.input} /></div>
              <div><label style={S.label}>Expected delivery</label><input type="date" value={form.expected_delivery} onChange={e => setForm(f=>({...f,expected_delivery:e.target.value}))} style={S.input} /></div>
              <div><label style={S.label}>Payment terms</label><input value={form.payment_terms} onChange={e => setForm(f=>({...f,payment_terms:e.target.value}))} style={S.input} placeholder="e.g. 15 days" /></div>
              <div><label style={S.label}>Prepared by *</label><input value={form.prepared_by} onChange={e => setForm(f=>({...f,prepared_by:e.target.value}))} style={S.input} placeholder="Your name" /></div>
              <div><label style={S.label}>GST % *</label><input type="number" value={form.gst_percent} onChange={e => setForm(f=>({...f,gst_percent:e.target.value}))} style={S.input} placeholder="Enter 0 if not applicable" /></div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
              <div>
                <label style={S.label}>Bill To</label>
                <textarea value={form.bill_to} onChange={e => setForm(f=>({...f,bill_to:e.target.value}))} style={{ ...S.input, height:80, resize:'vertical', fontSize:12 }} />
              </div>
              <div>
                <label style={S.label}>Ship To <span style={{ color:'#9ca3af', fontSize:11 }}>(edit if different from default)</span></label>
                <textarea value={form.ship_to} onChange={e => setForm(f=>({...f,ship_to:e.target.value}))} style={{ ...S.input, height:80, resize:'vertical', fontSize:12 }} />
              </div>
            </div>

            <div style={{ borderTop:'1px solid #e5e7eb', paddingTop:14, marginBottom:12 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                <div style={{ fontWeight:600, fontSize:14 }}>Materials to order</div>
                <button onClick={addItem} style={{ ...S.btnSm, background:'#f3f4f6', color:'#374151' }}>+ Add material</button>
              </div>
              {form.items.length === 0 ? (
                <div style={{ textAlign:'center', color:'#9ca3af', fontSize:13, padding:'16px 0' }}>Click "+ Add material" to start adding items</div>
              ) : (
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr>
                    {['Material *','Qty *','Unit','Price (₹)','HSN Code','Notes',''].map(h => <th key={h} style={{ ...S.th }}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {form.items.map((item,i) => (
                      <tr key={i}>
                        <td style={{ padding:'5px 4px' }}>
                          <div>
                            <div style={{ display:'flex', gap:4 }}>
                              <select value={item.raw_material_id} onChange={e => onMaterialSelect(i, e.target.value)} style={{ ...S.input, fontSize:12, minWidth:160 }}>
                                <option value="">Select material...</option>
                                {materials.map(m => <option key={m.id} value={m.id}>{m.name}{m.spec1?' · '+m.spec1:''}{m.spec2?' '+m.spec2:''}</option>)}
                              </select>
                              <button type="button" onClick={() => setShowNewMat(showNewMat===i?null:i)} style={{ ...S.btnSm, background:'#f3f4f6', fontSize:11, padding:'0 8px', whiteSpace:'nowrap' }}>+ New</button>
                            </div>
                            {showNewMat===i && (
                              <div style={{ background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:6, padding:10, marginTop:6 }}>
                                <div style={{ fontSize:11, fontWeight:600, color:'#374151', marginBottom:8 }}>New material type</div>
                                <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:6, marginBottom:8 }}>
                                  <div><input value={newMatForm.name} onChange={e=>setNewMatForm(p=>({...p,name:e.target.value}))} placeholder="Material name *" style={{ ...S.input, fontSize:12 }} /></div>
                                  <div><select value={newMatForm.category} onChange={e=>setNewMatForm(p=>({...p,category:e.target.value}))} style={{ ...S.input, fontSize:12 }}>{['Coil','Rubber','Fastener','Box','Chemical','Other'].map(c=><option key={c}>{c}</option>)}</select></div>
                                  <div><select value={newMatForm.unit} onChange={e=>setNewMatForm(p=>({...p,unit:e.target.value}))} style={{ ...S.input, fontSize:12 }}>{['kg','tons','pcs','m','rolls','ghonis'].map(u=><option key={u}>{u}</option>)}</select></div>
                                </div>
                                <div style={{ marginBottom:8 }}><input value={newMatForm.spec1} onChange={e=>setNewMatForm(p=>({...p,spec1:e.target.value}))} placeholder="Spec (optional)" style={{ ...S.input, fontSize:12, width:'100%' }} /></div>
                                <button type="button" onClick={async () => {
                                  if (!newMatForm.name.trim()) return alert('Name required');
                                  try { const { data } = await api.post('/inventory/materials', { ...newMatForm, consumption_mode:'manual', current_stock:0 }); const mats = await api.get('/inventory/materials'); setMaterials(mats.data); onMaterialSelect(i, String(data.id)); setShowNewMat(null); setNewMatForm({ name:'', category:'Coil', unit:'kg', spec1:'' }); } catch(e) { alert(e.response?.data?.error || 'Error'); }
                                }} style={{ ...S.btnPrimary, fontSize:12, padding:'4px 12px' }}>Save & select</button>
                                <button type="button" onClick={()=>setShowNewMat(null)} style={{ ...S.btnSecondary, fontSize:12, padding:'4px 10px', marginLeft:6 }}>Cancel</button>
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ padding:'5px 4px' }}><input type="number" value={item.qty_ordered} onChange={e => updateItem(i,'qty_ordered',e.target.value)} placeholder="Qty" style={{ ...S.input, fontSize:12, width:80 }} /></td>
                        <td style={{ padding:'5px 4px' }}><input value={item.unit} onChange={e => updateItem(i,'unit',e.target.value)} placeholder="Unit" style={{ ...S.input, fontSize:12, width:65 }} /></td>
                        <td style={{ padding:'5px 4px' }}><input type="number" value={item.unit_price} onChange={e => updateItem(i,'unit_price',e.target.value)} placeholder="0" style={{ ...S.input, fontSize:12, width:80 }} /></td>
                        <td style={{ padding:'5px 4px' }}><input value={item.hsn_code||''} onChange={e => updateItem(i,'hsn_code',e.target.value)} placeholder="e.g. 7318" style={{ ...S.input, fontSize:12, width:80 }} /></td>
                        <td style={{ padding:'5px 4px' }}><input value={item.notes||''} onChange={e => updateItem(i,'notes',e.target.value)} placeholder="optional" style={{ ...S.input, fontSize:12 }} /></td>
                        <td style={{ padding:'5px 4px' }}><button onClick={() => removeItem(i)} style={{ background:'none', border:'none', color:'#ef4444', cursor:'pointer', fontSize:18 }}>×</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ display:'flex', gap:10, paddingTop:16, borderTop:'1px solid #e5e7eb' }}>
              <button onClick={savePO} disabled={saving||!form.supplier_id||!form.po_number} style={S.btnPrimary}>{saving ? 'Saving...' : 'Save PO'}</button>
              <button onClick={() => setShowForm(false)} style={S.btnSecondary}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Supplier Form */}
      {showSupplierForm && (
        <div style={{ ...S.overlay, zIndex:1200 }}>
          <div style={{ ...S.modal, width:520 }}>
            <div style={{ fontSize:15, fontWeight:600, marginBottom:16 }}>Add supplier</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
              <Field label="Supplier name *" value={supForm.name} onChange={v => setSupForm(p=>({...p,name:v}))} />
              <Field label="Contact name" value={supForm.contact_name} onChange={v => setSupForm(p=>({...p,contact_name:v}))} />
              <Field label="Phone" value={supForm.phone} onChange={v => setSupForm(p=>({...p,phone:v}))} />
              <Field label="Email" value={supForm.email} onChange={v => setSupForm(p=>({...p,email:v}))} />
              <Field label="GST Number" value={supForm.gst_number} onChange={v => setSupForm(p=>({...p,gst_number:v}))} placeholder="e.g. 27AAAPJ8406B1Z6" />
              <Field label="Materials supplied" value={supForm.materials_supplied} onChange={v => setSupForm(p=>({...p,materials_supplied:v}))} placeholder="e.g. MS coils, EPDM rubber" />
              <div style={{ gridColumn:'span 2' }}>
                <label style={S.label}>Address</label>
                <textarea value={supForm.address} onChange={e => setSupForm(p=>({...p,address:e.target.value}))} style={{ ...S.input, height:64, resize:'vertical', fontSize:12 }} placeholder="Full supplier address" />
              </div>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={saveSupplier} disabled={!supForm.name} style={S.btnPrimary}>Save supplier</button>
              <button onClick={() => setShowSupplierForm(false)} style={S.btnSecondary}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* PO Detail Modal */}
      {showDetail && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, width:720 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
              <div>
                <div style={{ fontSize:16, fontWeight:600 }}>{showDetail.po.po_number}</div>
                <div style={{ fontSize:13, color:'#6b7280', marginTop:2 }}>{showDetail.po.supplier_name} · {showDetail.po.po_date}</div>
                {showDetail.po.payment_terms && <div style={{ fontSize:12, color:'#6b7280' }}>Payment: {showDetail.po.payment_terms}</div>}
                {showDetail.po.expected_delivery && <div style={{ fontSize:12, color:'#6b7280' }}>Expected: {showDetail.po.expected_delivery}</div>}
              </div>
              <div style={{ display:'flex', gap:8 }}>
                {showDetail.po.status === 'draft' && (
                  <button onClick={() => markSent(showDetail.po.id)} style={{ ...S.btnSm, background:'#3b82f6', color:'#fff' }}>Mark as sent</button>
                )}
                {showDetail.po.status !== 'received' && (
                  <button onClick={() => setShowReceipt(showDetail)} style={{ ...S.btnSm, background:'#10b981', color:'#fff' }}>+ Record receipt</button>
                )}
                <button onClick={() => downloadPDF(showDetail.po.id)} style={{ ...S.btnSm, background:'#1a1f2e', color:'#fff' }}>⬇ Download PDF</button>
                <button onClick={() => setShowDetail(null)} style={S.btnSecondary}>Close</button>
              </div>
            </div>

            <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:16 }}>
              <thead><tr>{['Material','Spec','Ordered','Received','Pending','Unit price','Notes'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>
                {showDetail.items.map(item => (
                  <tr key={item.id} style={{ borderBottom:'1px solid #f3f4f6' }}>
                    <td style={{ ...S.td, fontWeight:500 }}>{item.material_name}</td>
                    <td style={{ ...S.td, color:'#6b7280', fontSize:12 }}>{[item.spec1,item.spec2,item.spec3].filter(Boolean).join(' ')}</td>
                    <td style={S.td}>{item.qty_ordered} {item.unit}</td>
                    <td style={{ ...S.td, color: item.qty_received >= item.qty_ordered ? '#10b981':'#f59e0b', fontWeight:500 }}>{item.qty_received}</td>
                    <td style={{ ...S.td, color: item.qty_ordered-item.qty_received > 0 ? '#ef4444':'#10b981' }}>{Math.max(0,item.qty_ordered-item.qty_received)}</td>
                    <td style={{ ...S.td, color:'#6b7280' }}>{item.unit_price ? `₹${item.unit_price}` : '—'}</td>
                    <td style={{ ...S.td, color:'#6b7280', fontSize:12 }}>{item.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {showDetail.receipts.length > 0 && (
              <div>
                <div style={{ fontWeight:600, fontSize:13, marginBottom:8 }}>Goods receipts</div>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr>{['Date','Qty received','Challan no.','Invoice no.','Notes'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {showDetail.receipts.map(r => (
                      <tr key={r.id} style={{ borderBottom:'1px solid #f3f4f6' }}>
                        <td style={{ ...S.td, fontSize:12 }}>{r.receipt_date}</td>
                        <td style={{ ...S.td, color:'#10b981', fontWeight:500 }}>+{r.qty_received}</td>
                        <td style={{ ...S.td, fontSize:12, color:'#6b7280' }}>{r.challan_number||'—'}</td>
                        <td style={{ ...S.td, fontSize:12, color:'#6b7280' }}>{r.invoice_number||'—'}</td>
                        <td style={{ ...S.td, fontSize:12, color:'#6b7280' }}>{r.notes||'—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      {showReceipt && (
        <div style={{ ...S.overlay, zIndex:1100 }}>
          <div style={{ ...S.modal, width:500 }}>
            <div style={{ fontSize:15, fontWeight:600, marginBottom:16 }}>Record goods receipt</div>
            <div style={{ marginBottom:12 }}>
              <label style={S.label}>Which material arrived? *</label>
              <select value={receipt.po_item_id} onChange={e => setReceipt(r=>({...r,po_item_id:e.target.value}))} style={S.input}>
                <option value="">Select item...</option>
                {showReceipt.items.map(i => (
                  <option key={i.id} value={i.id}>
                    {i.material_name} — ordered {i.qty_ordered} {i.unit}, received {i.qty_received} so far
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
              <Field label="Qty received *" value={receipt.qty_received} onChange={v => setReceipt(r=>({...r,qty_received:v}))} type="number" />
              <div><label style={S.label}>Receipt date *</label><input type="date" value={receipt.receipt_date} onChange={e => setReceipt(r=>({...r,receipt_date:e.target.value}))} style={S.input} /></div>
              <Field label="Challan number" value={receipt.challan_number} onChange={v => setReceipt(r=>({...r,challan_number:v}))} placeholder="DC/challan no." />
              <Field label="Invoice number" value={receipt.invoice_number} onChange={v => setReceipt(r=>({...r,invoice_number:v}))} placeholder="Supplier invoice no." />
            </div>
            <div style={{ marginBottom:16 }}><label style={S.label}>Notes</label><input value={receipt.notes} onChange={e => setReceipt(r=>({...r,notes:e.target.value}))} style={S.input} placeholder="e.g. Short delivery, quality issue" /></div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={saveReceipt} disabled={!receipt.po_item_id||!receipt.qty_received} style={{ ...S.btnPrimary, background:'#10b981' }}>Save receipt & update stock</button>
              <button onClick={() => setShowReceipt(null)} style={S.btnSecondary}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
